<template>
  <div class="mole-game">
    <LevelComplete v-if="isGameComplete" />

    <div v-else>
      <h1 class="instruction">
        Strike the {{ targetItem }}
      </h1>
      <div class="grid">
        <div
          v-for="(itemObj, index) in displayedItems"
          :key="index"
          class="item-hole"
          :style="{
            backgroundColor:
              itemObj.item === targetItem && correctClickIndex === index
                ? 'lightgreen'
                : '#f3e5f5',
          }"
          :class="{ disabled: itemObj.clicked }"
          @click="handleClick(index)"
        >
          <div class="item-hole-content">
            <span
              v-if="itemObj.item"
              :class="{ 'target-item': itemObj.item === targetItem }"
            >
              {{ getItemDisplay(itemObj.item) }}
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="message"
        class="message"
      >
        {{ message }}
      </div>
    </div>
  </div>
</template>

<script>
import { speechMixin } from "@/mixins/speechMixin.js";

import LevelComplete from "../LevelComplete.vue";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  props: {
    selectedCategory: {
      type: String,
      required: true,
      default: "vowels",
    },
  },
  data() {
    return {
      categories: {
        vowels: ["A", "E", "I", "O", "U"],
        alphabet: "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""),
        numbers: "0123456789".split(""),
        fruitsAndVeggies: [
          "Apple",
          "Banana",
          "Carrot",
          "Lettuce",
          "Orange",
          "Tomato",
          "Broccoli",
          "Mango",
          "Pear",
          "Peach",
        ],
      },
      emojiMapping: {
        Apple: "🍎",
        Banana: "🍌",
        Carrot: "🥕",
        Lettuce: "🥬",
        Orange: "🍊",
        Tomato: "🍅",
        Broccoli: "🥦",
        Mango: "🥭",
        Pear: "🍐",
        Peach: "🍑",
      },
      targetItem: "",
      message: "",
      itemCounts: {},
      maxCount: 3,
      isGameComplete: false,
      displayedItems: [],
      intervalId: null,
      correctClickIndex: null,
      screenWidth: window.innerWidth,
    };
  },
  computed: {
    currentCategory() {
      return this.categories[this.selectedCategory];
    },
    displayItemType() {
      switch (this.selectedCategory) {
        case "numbers":
          return "number";
        case "fruitsAndVeggies":
          return "fruit or vegetable";
        default:
          return "letter";
      }
    },
    numberOfCards() {
      return this.screenWidth <= 768 ? 6 : 9;
    },
  },
  watch: {
    numberOfCards(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.initializeDisplayedItems();
        this.updateItems();
      }
    },
    selectedCategory(newCategory, oldCategory) {
      if (newCategory !== oldCategory) {
        this.initializeItemCounts();
        this.setupGame();
      }
    },
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    this.initializeItemCounts();
    this.setupVoices();
    this.setupGame();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onResize);
    clearInterval(this.intervalId); // Ensure interval is cleared when component unmounts
  },
  methods: {
    onResize() {
      this.screenWidth = window.innerWidth;
    },
    initializeItemCounts() {
      this.itemCounts = this.currentCategory.reduce((acc, item) => {
        acc[item] = 0;
        return acc;
      }, {});
    },
    setupGame() {
      this.initializeDisplayedItems();
      this.setTargetItem();
      this.intervalId = setInterval(this.updateItems, 2000);
    },
    initializeDisplayedItems() {
      this.displayedItems = Array.from({ length: this.numberOfCards }, () => ({
        item: null,
        clicked: false,
      }));
    },
    setTargetItem() {
      this.targetItem = this.currentCategory.find(
        (item) => this.itemCounts[item] < this.maxCount
      );

      if (!this.targetItem) {
        this.isGameComplete = true;
        this.playSound(
          "https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3"
        );
        clearInterval(this.intervalId);
        this.speakOutLoud("Great Job!");
        // Redirect to the appropriate route based on selectedCategory
        setTimeout(() => {
          this.$router.push(this.getRedirectRoute());
        }, 4000);
        return;
      }

      this.speakOutLoud(`Strike the ${this.targetItem}!`);
    },
    updateItems() {
      if (this.isGameComplete) {
        clearInterval(this.intervalId);
        return;
      }
      this.displayedItems = Array.from({ length: this.numberOfCards }, () => {
        const randomItem =
          this.currentCategory[
            Math.floor(Math.random() * this.currentCategory.length)
          ];
        return Math.random() > 0.5
          ? { item: randomItem, clicked: false }
          : { item: null, clicked: false };
      });

      const randomIndex = Math.floor(
        Math.random() * this.displayedItems.length
      );
      this.displayedItems[randomIndex] = {
        item: this.targetItem,
        clicked: false,
      };
      this.correctClickIndex = null;
    },
    handleClick(index) {
      const itemObj = this.displayedItems[index];

      if (itemObj.clicked) {
        return; // Item already clicked, do nothing
      }

      if (itemObj.item === this.targetItem) {
        itemObj.clicked = true;
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );
        this.itemCounts[itemObj.item]++;
        this.correctClickIndex = index;
        this.message = "✅";
        if (this.itemCounts[itemObj.item] >= this.maxCount) {
          // Delay updating the target item to allow the last item to turn green
          setTimeout(() => {
            this.setTargetItem();
          }, 1000);
        }
      } else {
        this.message = "❌";
      }
      setTimeout(() => {
        this.message = "";
        this.correctClickIndex = null;
      }, 1000);
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    getItemDisplay(item) {
      if (this.selectedCategory === "fruitsAndVeggies") {
        return this.emojiMapping[item] || item;
      } else {
        return item;
      }
    },
    getRedirectRoute() {
      const routeMap = {
        vowels: "/letters",
        alphabet: "/letters",
        numbers: "/numbers",
        fruitsAndVeggies: "/fruits-and-veggies",
      };
      return routeMap[this.selectedCategory] || "/";
    },
  },
};
</script>

<style scoped>
.mole-game {
  text-align: center;
  padding: 10px;
}

.grid {
  max-width: 400px;
  display: grid;
  grid-template-columns: repeat(3, minmax(80px, 1fr));
  gap: 20px;
  margin: auto;
}

.item-hole {
  background-color: #f3e5f5;
  position: relative;
  width: 100%;
  cursor: pointer;
  transition: transform 0.3s, background-color 0.3s;
  border-radius: 15px;
}

.item-hole::before {
  content: "";
  display: block;
  padding-top: 100%; /* Maintains a square aspect ratio */
}

.item-hole:hover {
  transform: scale(1.05);
}

.item-hole-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 2rem;
  padding: 10px;
  word-wrap: break-word;
  text-align: center;
}

.target-item {
  color: #ff4081;
  font-weight: bold;
}

.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.message {
  font-size: 2rem;
  margin-top: 20px;
}

.instruction {
  font-size: 4rem;
  margin-bottom: 20px;
}

/* Media queries for responsiveness */
@media (max-width: 768px) {
  .grid {
    grid-template-columns: repeat(2, minmax(80px, 1fr));
    gap: 20px;
  }
  .item-hole-content {
    font-size: 4rem;
    padding: 8px;
  }

  .instruction,
  .message {
    font-size: 2rem;
  }
}

@media (max-width: 480px) {
  .grid {
    grid-template-columns: repeat(2, minmax(50px, 1fr));
    gap: 15px;
  }

  .item-hole-content {
    font-size: 2rem;
    padding: 5px;
  }

  .instruction,
  .message {
    font-size: 2rem;
  }
}
</style>